import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './StaffGenderMismatchBlocker.module.css';

/**
 * AB#1842 (PBI-F) — Owner-side modal: submit a Super Admin override request
 * for ONE specific staff member who is blocking the salon's ServiceType
 * flip. The owner explains why the staff stays despite the mismatch (≥ 20
 * chars). On submit the parent POSTs to
 * `/super-admin/businesses/{id}/staff/{staffId}/gender-mismatch-override`
 * via `apiService.requestGenderOverride` (see Faris brief §3).
 *
 * After a successful submit the parent toasts the localized
 * `business.serviceType.flip.override.submitted` string and closes both
 * this modal AND the parent blocking dialog — the owner is told to wait
 * for the Super Admin's email decision.
 */

export interface SuperAdminOverrideRequestModalProps {
    isOpen: boolean;
    /** Display-only — shown in the prompt copy so the owner is reminded
     *  WHICH staff this override applies to. */
    staffName: string;
    isSubmitting: boolean;
    onCancel: () => void;
    onSubmit: (justification: string) => void;
}

/** Same length floor that the BE enforces (Faris brief §3 — Validator
 *  rule `Justification MinLength 20`). Mirrors PBI-G's consent text floor
 *  pattern but distinct enough to not collide. */
const MIN_JUSTIFICATION_LEN = 20;

function interpolate(template: string, vars: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (_m, k) => (vars[k] == null ? '' : String(vars[k])));
}

const SuperAdminOverrideRequestModal: React.FC<SuperAdminOverrideRequestModalProps> = ({
    isOpen,
    staffName,
    isSubmitting,
    onCancel,
    onSubmit,
}) => {
    const { t } = useLanguage();
    const [text, setText] = useState('');
    const [submitAttempted, setSubmitAttempted] = useState(false);

    if (!isOpen) return null;

    const trimmedLen = text.trim().length;
    const isReady = trimmedLen >= MIN_JUSTIFICATION_LEN;
    const showError = submitAttempted && !isReady;

    const handleSubmit = () => {
        setSubmitAttempted(true);
        if (!isReady || isSubmitting) return;
        onSubmit(text.trim());
    };

    return (
        <div className={styles.scrim} role="presentation">
            <div
                className={`${styles.dialog} ${styles.dialogCompact}`}
                role="alertdialog"
                aria-labelledby="override-request-title"
                aria-describedby="override-request-prompt"
            >
                <div className={styles.header}>
                    <span className={`${styles.iconWrap} ${styles.iconWrapInfo}`} aria-hidden="true">
                        ?
                    </span>
                    <h2 id="override-request-title" className={styles.title}>
                        {t('business.serviceType.flip.override.title')}
                    </h2>
                </div>

                <div className={styles.body}>
                    <p id="override-request-prompt" className={styles.bodyText}>
                        {staffName ? `${staffName} — ` : ''}
                        {t('business.serviceType.flip.override.prompt')}
                    </p>
                    <div className={styles.textareaWrap}>
                        <textarea
                            className={styles.textarea}
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder={t('business.serviceType.flip.override.placeholder')}
                            aria-required="true"
                            aria-invalid={showError || undefined}
                            aria-describedby="override-request-counter"
                            disabled={isSubmitting}
                            autoFocus
                        />
                        <span
                            id="override-request-counter"
                            className={`${styles.counter} ${isReady ? styles.counterReady : ''}`}
                        >
                            {interpolate(
                                t('business.serviceType.flip.override.counter'),
                                { count: trimmedLen },
                            )}
                        </span>
                        {showError && (
                            <p className={styles.errorText} role="alert">
                                {t('business.serviceType.flip.override.tooShort')}
                            </p>
                        )}
                    </div>
                    <p className={styles.bodyInstructions}>
                        {t('business.serviceType.flip.override.disclaimer')}
                    </p>
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerRight}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {t('business.serviceType.flip.override.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? '…'
                                : t('business.serviceType.flip.override.submit')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminOverrideRequestModal;
